import { useMemo, useState } from 'react'

import { SecondaryActionItem } from '@factorialco/f0-react/dist/experimental'
import { ArrowCycle, Upload } from '@factorialco/f0-react/icons/app'

import { track } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { Modals, ModalsProps } from 'modules/integrations/components/SyncButton/Modals'
import { usePrivateConditionalIntegrationsSyncContext } from 'modules/integrations/hooks/SyncContext'

/** Shape compatible with both SecondaryActionItem (DataCollection) and header secondaryActions. */
export type SyncActionBase = Pick<
  SecondaryActionItem,
  'label' | 'icon' | 'loading' | 'onClick' | 'disabled'
> & {
  onClick: () => void
  tooltip?: string
}

export type UseSyncActionProps = {
  onConfirmSync?: ModalsProps['onConfirm']
  confirmTrackingEvent?: string
  disabled?: boolean
  setOpenCustomModal?: (open: boolean) => void
  hidden?: boolean
  tooltip?: string
}

type UseSyncActionReturn = {
  syncActions: SyncActionBase[]
  syncModal: React.ReactNode | null
}

export const usePrivateSyncAction = ({
  onConfirmSync,
  confirmTrackingEvent,
  disabled = false,
  setOpenCustomModal,
  hidden = false,
  tooltip,
}: UseSyncActionProps = {}): UseSyncActionReturn => {
  const context = usePrivateConditionalIntegrationsSyncContext(hidden)
  const capabilityMode = context?.capabilityMode
  const syncEnabled = context?.syncEnabled ?? false
  const marketplaceIntegration = context?.marketplaceIntegration
  const isSyncRunning = context?.isSyncRunning ?? false
  const [showModal, setShowModal] = useState(false)

  const isExportMode = capabilityMode === 'file'
  const canShowSyncAction = !hidden && syncEnabled && marketplaceIntegration

  const syncActions = useMemo<SyncActionBase[]>(() => {
    if (canShowSyncAction) {
      return [
        {
          tooltip,
          label: isExportMode
            ? i18n.t('compensations.integrations.export_action', {
                integration_name: marketplaceIntegration.displayName,
              })
            : i18n.t('compensations.integrations.sync_action', {
                integration_name: marketplaceIntegration.displayName,
              }),
          icon: isExportMode ? Upload : ArrowCycle,
          loading: isSyncRunning,
          onClick: () => {
            if (setOpenCustomModal) {
              setOpenCustomModal(true)
            } else {
              setShowModal(true)
            }
            if (confirmTrackingEvent) track(confirmTrackingEvent)
          },
          disabled,
        },
      ]
    }
    return []
  }, [
    canShowSyncAction,
    marketplaceIntegration,
    isExportMode,
    isSyncRunning,
    confirmTrackingEvent,
    disabled,
    setOpenCustomModal,
    tooltip,
  ])

  return {
    syncActions: syncActions,
    syncModal: showModal ? <Modals setShowModal={setShowModal} onConfirm={onConfirmSync} /> : null,
  }
}
